import React, { useState } from 'react';
import { t, TranslationKey } from '../../../../i18n';
import { UserIcon, AssistantIcon } from './utils/Icons';
import BubblesContainer from './BubblesContainer';
import { Message, ConversationObj, MainChatProps, VariantSelectorMessageProps } from '../types';
import VariantSelectorMessage from './VariantSelectorMessage';
import SubtaskSelectorMessage from './SubtaskSelectorMessage';
import TaskSuggestions from './TaskSuggestions';
import TaskSummaryMessage from '../Components/TaskSummaryMessage';
import UserMessage from './UserMessage';
import WellDefinedTaskSuggestions from './WellDefinedTaskSuggestions';

const MainChat: React.FC<MainChatProps> = ({ messages, messagesEndRef, formatTime,
   lang, onSelectTask, onSelectMultipleTasks, onContinueSelection, onSelectVariant, onSelectSubtasks, currentConversation, onUpdateMessage, triggerConversationUpdate, onWellDefinedTaskSelected, onConfirmTasks }) => {
  const isSubmitted = currentConversation?.isSubmitted;
  const isWelcomeStage = currentConversation?.conversationDialogueState === 'conv_welcome';
  const isTaskRequestStage = currentConversation?.conversationDialogueState === 'conv_task_request';
  const isWellDefinedTasksExplanationStage = currentConversation?.conversationDialogueState === 'well_defined_tasks_explanation';
  // Local state for amount fields, keyed by message id
  // const [amountValues, setAmountValues] = useState<Record<string, string>>({});

  // Billing label mapping for i18n
  const billingLabelMap: Record<string, { en: string; nl: string }> = {
    billable_unit: { en: 'Billable unit', nl: 'Facturatie-eenheid' },
    quantity_result_obligation: { en: 'Quantity result obligation', nl: 'Resultaatsverplichting hoeveelheid' },
    price_per_unit_in_euro: { en: 'Price per unit (€)', nl: 'Prijs per eenheid (€)' },
  };

  return (
    <div className="main-chat">
      <div className="chat-messages">
          <div className="messages-list">
            {messages.map((message, idx) => {
              // Check if previous message is from the same sender
              const previousMessage = idx > 0 ? messages[idx - 1] : null;
              const isConsecutiveMessage = Boolean(previousMessage && 
                ((message.isAssistant && previousMessage.isAssistant) || 
                 (!message.isAssistant && !previousMessage.isAssistant)));
              
              // Determine if we should show the header
              const showHeader = !isConsecutiveMessage;
              if (message.messageType === 'variantSelection') {
                return (
                  <VariantSelectorMessage
                    key={message.id}
                    message={message}
                    onSelectVariant={onSelectVariant}
                    selectedVariantId={message.selectedVariantId}
                    lang={lang}
                  />
                );
              } else if (message.messageType === 'subtaskSelection') {
                return (
                  <div key={message.id} className="message assistant">
                    <div className="message-content">
                      {showHeader && (
                        <div className="message-header">
                          <span className="message-sender">
                            <AssistantIcon />
                            <span className="sender-name">{t(lang, 'assistant')}</span>
                          </span>
                          <span className="message-time">
                            {message.timestamp instanceof Date && !isNaN(message.timestamp.getTime()) ? formatTime(message.timestamp) : ''}
                          </span>
                        </div>
                      )}
                      <div className="message-text">{message.text}</div>
                      {onSelectSubtasks && (
                        <SubtaskSelectorMessage
                          message={message}
                          lang={lang}
                          onSelectSubtasks={onSelectSubtasks}
                        />
                      )}
                    </div>
                  </div>
                );
              } else if (message.messageType === 'assistant') {

                // Assistant message rendering (directly from message fields)
                // CRITICAL: Add special styling for new subtask messages
                const isNewSubtaskMessage = message.isNewSubtask || false;
                return (
                  <div 
                    key={message.id} 
                    className={`message assistant ${isNewSubtaskMessage ? 'new-subtask-message' : ''}`}
                    style={{ 
                      marginTop: isConsecutiveMessage ? '0px' : (isNewSubtaskMessage ? '16px' : '1px'),
                      marginBottom: isConsecutiveMessage ? '0px' : (isNewSubtaskMessage ? '8px' : '1px'),
                      paddingTop: isConsecutiveMessage ? '0px' : (isNewSubtaskMessage ? '12px' : undefined),
                      paddingBottom: isConsecutiveMessage ? '0px' : (isNewSubtaskMessage ? '12px' : undefined)
                    }}
                  >
                    <div className="message-content">
                      {showHeader && (
                        <div className="message-header">
                          <span className="message-sender">
                            <AssistantIcon />
                            <span className="sender-name">{t(lang, 'assistant')}</span>
                          </span>
                          <span className="message-time">
                            {message.timestamp instanceof Date && !isNaN(message.timestamp.getTime()) ? formatTime(message.timestamp) : ''}
                          </span>
                        </div>
                      )}
                      {/* Only show the label and skip first line if the message is a normalized request */}
                      {message.text && message.text.startsWith('Normalized request:') && (
                        <div className="bubble-label" style={{ marginBottom: '2px' }}>{t(lang, 'normalizedRequest')}</div>
                      )}
                      <div className="message-text">{
                        message.selectedTaskId && message.taskDescription && message.taskSimilarity !== undefined
                          ? (
                              <>
                                {lang === 'nl' ? 'Taak ID:' : 'Task ID:'} {message.selectedTaskId}<br />
                                {lang === 'nl' ? 'Beschrijving:' : 'Description:'} {message.taskDescription}<br />
                                {lang === 'nl' ? 'Gelijkenis:' : 'Similarity:'} {typeof message.taskSimilarity === 'number' ? message.taskSimilarity.toFixed(2) : message.taskSimilarity}<br />
                                {message.billing?.[message.selectedTaskId]?.billable_unit && (
                                  <>
                                    <br />
                                    <strong>Billable unit:</strong> {message.billing[message.selectedTaskId].billable_unit}<br />
                                  </>
                                )}
                              </>
                            )
                          : (
                              message.text
                                ? (message.text.startsWith('Normalized request:')
                                    ? message.text.split('\n').slice(1).map((line, idx) => (
                                        <React.Fragment key={idx}>
                                          {line}
                                          <br />
                                        </React.Fragment>
                                      ))
                                    : <div dangerouslySetInnerHTML={{ __html: message.text }} /> // Render HTML content
                                  )
                                : t(lang, (message.textKey as TranslationKey) || 'assistantDefault')
                            )
                      }</div>
                      {/* Show bubbles for assistant messages with keywords or extracted values UNDER the normalized message */}
                      {(message.keywords || message.extractedValues) && (
                        <BubblesContainer keywords={message.keywords} extractedValues={message.extractedValues} lang={lang} />
                      )}
                      {/* Billing section as bubbles */}
                      {message.billing && message.selectedTaskId && message.billing[message.selectedTaskId] && (
                        <div style={{ marginTop: 8 }}>
                          <div className="bubble-label" style={{ marginBottom: '2px' }}>{t(lang, 'billing')}</div>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                            {Object.entries(message.billing[message.selectedTaskId]).map(([key, value]) => {
                              const label = billingLabelMap[key]?.[lang] || key;
                              return (
                                <span key={key} className="bubble extracted-value-bubble" style={{ background: '#e6d6f7', color: '#6c3483' }}>
                                  {label}: {value}
                                </span>
                              );
                            })}
                          </div>
                        </div>
                      )}
                      {/* Amount section as bubbles (editable) */}
                      {message.finalTaskId && (
                        <div style={{ marginTop: 8 }}>
                          <div className="bubble-label" style={{ marginBottom: '2px' }}>{t(lang, 'amount')}</div>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                            {message.amount ? (
                              <span className="bubble extracted-value-bubble" style={{ background: '#e6d6f7', color: '#6c3483' }}>
                                {message.amount}
                              </span>
                            ) : (
                              <input
                                type="text"
                                placeholder={t(lang, 'amount')}
                                value={message.amount ?? '1'}
                                onChange={e => onUpdateMessage(message.id, { amount: e.target.value })}
                                onBlur={e => onUpdateMessage(message.id, { amount: e.target.value })}
                                onKeyDown={e => {
                                  if (e.key === 'Enter') {
                                    (e.target as HTMLInputElement).blur();
                                  }
                                }}
                                style={{
                                  padding: '6px 16px',
                                  borderRadius: 16,
                                  border: '1px solid #c7a8ff',
                                  background: '#f3eaff',
                                  color: '#6c3483',
                                  fontSize: '1rem',
                                  outline: 'none',
                                  minWidth: 80,
                                }}
                              />
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              } else if (message.messageType === 'assistantClassification') {
                // Find the task for this message
                const task = currentConversation?.tasks.find(t => t.id === message.taskId);
                return (
                  <div 
                    key={message.id} 
                    className={`message assistant-classification`}
                    style={{ 
                      marginTop: isConsecutiveMessage ? '0px' : '1px',
                      marginBottom: isConsecutiveMessage ? '0px' : '1px',
                      paddingTop: isConsecutiveMessage ? '0px' : undefined,
                      paddingBottom: isConsecutiveMessage ? '0px' : undefined
                    }}
                  >
                    <div className="message-content">
                      {showHeader && (
                        <div className="message-header">
                          <span className="message-sender">
                            <AssistantIcon />
                            <span className="sender-name">{t(lang, 'assistant')}</span>
                          </span>
                          <span className="message-time">
                            {message.timestamp instanceof Date && !isNaN(message.timestamp.getTime()) ? formatTime(message.timestamp) : ''}
                          </span>
                        </div>
                      )}
                      <div className="message-text">{message.text}</div>
                      {/* Show bubbles for assistant-classification messages based on task properties */}
                      {task && (task.keywords || task.actions || task.measurements || task.location || task.employeeCount !== undefined) && (
                        <BubblesContainer 
                          keywords={task.keywords} 
                          actions={task.actions}
                          extractedValues={task.measurements} 
                          location={task.location}
                          employeeCount={task.employeeCount}
                          lang={lang} 
                        />
                      )}
                    </div>
                  </div>
                )
              } else if (message.messageType === 'taskSelection') {
                // Render all taskSelection messages with the same parentId as a single TaskSuggestions component
                const taskSuggestions = messages.filter(
                  m => m.messageType === 'taskSelection' && m.parentId === message.parentId
                );
                // Only render for the first occurrence in the group
                if (
                  taskSuggestions.length > 0 &&
                  taskSuggestions[0].id === message.id
                ) {
                  return (
                    <TaskSuggestions
                      key={taskSuggestions.map(m => m.id).join('-')}
                      tasks={taskSuggestions}
                      selectedTaskId={message.selectedTaskId ?? null}
                      // selectedTaskIds are only persisted for well-defined tasks;
                      // for regular tasks, TaskSuggestions manages selection locally.
                      selectedTaskIds={(() => {
                        const parentMessage = messages.find(m => m.id === message.parentId);
                        return parentMessage?.selectedTaskIds ?? [];
                      })()}
                      onSelectTask={(taskId) => onSelectTask(message.parentId!, taskId)}
                      onSelectMultipleTasks={onSelectMultipleTasks ? (taskIds) => onSelectMultipleTasks(message.parentId!, taskIds) : undefined}
                      onContinue={onContinueSelection ? (selectedIds) => {
                        console.log('🟧 [MainChat] onContinueSelection wrapper called', {
                          parentId: message.parentId,
                          selectedTaskIds: selectedIds,
                          selectedCount: selectedIds.length,
                          timestamp: new Date().toISOString()
                        });
                        onContinueSelection(message.parentId!, selectedIds);
                      } : undefined}
                      lang={lang}
                    />
                  );
                } else {
                  return null;
                }
              } else if (message.messageType === 'task_summary') {
                return (
                  <TaskSummaryMessage
                    key={message.id}
                    tasks={message.taskData || []}
                    timestamp={message.timestamp}
                    lang={lang}
                    isEditable={message.isEditable !== false}
                    messageId={message.id}
                    onSubmit={(messageId, updatedTasks) => {
                      if (onUpdateMessage) {
                        // Update the message with the new task data and mark as not editable
                        onUpdateMessage(messageId, {
                          taskData: updatedTasks,
                          isEditable: false
                        });
                        // Trigger conversation update to persist changes
                        if (triggerConversationUpdate) {
                          triggerConversationUpdate();
                        }
                      }
                    }}
                    onConfirm={() => {
                      if (onConfirmTasks) {
                        onConfirmTasks();
                      }
                    }}
                    onTaskRemove={(messageId, taskId) => {
                      if (onUpdateMessage) {
                        // Find the message and remove the task from its taskData
                        const message = messages.find(m => m.id === messageId);
                        if (message && message.taskData) {
                          const updatedTasks = message.taskData.filter((t: any) => t.id !== taskId);
                          onUpdateMessage(messageId, {
                            taskData: updatedTasks,
                            removedTaskId: taskId // Pass the removed task ID for cleanup
                          });
                        }
                      }
                    }}
                  />
                );
              } else if (message.messageType === 'user') {
                return (
                  <UserMessage key={message.id} message={message} formatTime={formatTime} showHeader={showHeader} isConsecutiveMessage={isConsecutiveMessage} />
                );
              } else {
                return null;
              }
            })}
            {/* Show well-defined task suggestions in welcome, task request, or well-defined-tasks-explanation stages */}
            {messages.length > 0 && onWellDefinedTaskSelected && (
              (() => {
                const lastMessage = messages[messages.length - 1];

                if (isWelcomeStage) {
                  const isWelcomeMessage = lastMessage.isAssistant && (
                    lastMessage.textKey === 'conv_welcome' ||
                    (messages.filter(m => m.isAssistant).length === 1 && lastMessage.isAssistant)
                  );
                  const hasUserMessages = messages.some(m => !m.isAssistant);
                  if (!isWelcomeMessage || hasUserMessages) {
                    return null;
                  }
                } else if (isTaskRequestStage) {
                  const isTaskRequestPrompt = lastMessage.isAssistant && (
                    lastMessage.textKey === 'conv_task_request'
                  );
                  if (!isTaskRequestPrompt) {
                    return null;
                  }
                } else if (isWellDefinedTasksExplanationStage) {
                  const isWellDefinedTasksMessage = lastMessage.isAssistant && (
                    lastMessage.textKey === 'well_defined_tasks_explanation'
                  );
                  if (!isWellDefinedTasksMessage) {
                    return null;
                  }
                } else {
                  return null;
                }

                return (
                  <WellDefinedTaskSuggestions
                    key="well-defined-task-suggestions"
                    lang={lang}
                    onSelectTask={onWellDefinedTaskSelected}
                  />
                );
              })()
            )}
            <div ref={messagesEndRef} data-messages-end />
            {isSubmitted && (
              <div className="chat-submitted-banner">
                <hr className="chat-submitted-separator" />
                <div className="chat-submitted-text">
                  {t(lang, 'chatSubmittedNotice')}
                </div>
              </div>
            )}
          </div>
      </div>
    </div>
  );
};

export default MainChat; 